import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, NavLink, Outlet, useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import { clearSession, isLoggedIn, posStaffSession } from '../../api';
import { can, errorText, PosMe, posApi } from './api';
import './pos.css';

export interface PosContext {
  me: PosMe;
  reloadMe: () => void;
  // Keeps the admin console's ?restoId= on POS links (admins act on a venue).
  to: (path: string) => string;
}

export const usePos = () => useOutletContext<PosContext>();

// Shell for /pos/*: the venue owner (partner login) or a staff PIN login. Navigation follows the role.
const PosLayout: React.FC = () => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const [me, setMe] = useState<PosMe | null>(null);
  const [error, setError] = useState<string | null>(null);
  const signedIn = isLoggedIn() || !!posStaffSession();
  const restoId = new URLSearchParams(search).get('restoId');
  const to = useCallback((path: string) => (restoId ? `${path}${path.includes('?') ? '&' : '?'}restoId=${restoId}` : path), [restoId]);

  const reloadMe = useCallback(() => {
    posApi.me().then(setMe).catch((err) => setError(errorText(err, 'Could not open the POS. Your session may have expired.')));
  }, []);
  useEffect(() => { if (signedIn) reloadMe(); }, [signedIn, reloadMe]);

  if (!signedIn) return <Navigate to="/pos/login" replace />;

  const logout = () => {
    const staff = !!posStaffSession();
    clearSession();
    navigate(staff ? '/pos/login' : '/');
  };

  if (error) {
    return (
      <div className="pos"><div className="pos-main" style={{ maxWidth: 480, marginTop: 80 }}>
        <div className="pos-msg err">{error}</div>
        <button className="pos-btn gold" onClick={logout}>Sign in again</button>
      </div></div>
    );
  }
  if (!me) return <div className="pos"><div className="pos-main pos-muted">Opening the POS...</div></div>;

  return (
    <div className="pos">
      <header className="pos-top">
        <div className="pos-brand">C2N POS<small>{me.venueName}</small></div>
        <nav className="pos-nav" aria-label="POS">
          <NavLink to={to('/pos')} end>Floor</NavLink>
          <NavLink to={to('/pos/orders')}>Orders</NavLink>
          {can(me.role, 'Manager') && <NavLink to={to('/pos/reports')}>Reports</NavLink>}
          {can(me.role, 'Manager') && <NavLink to={to('/pos/day-close')}>Day Close</NavLink>}
          {can(me.role, 'Manager') && <NavLink to={to('/pos/setup')}>Setup</NavLink>}
        </nav>
        <div className="pos-user">
          <span>{me.name}<br /><small>{me.role}</small></span>
          {me.role === 'Owner' && <button className="pos-btn sm" onClick={() => navigate(restoId ? '/admin' : '/partner-account')}>Portal</button>}
          <button className="pos-btn sm red" onClick={logout}>Logout</button>
        </div>
      </header>
      <main className="pos-main">
        <Outlet context={{ me, reloadMe, to } satisfies PosContext} />
      </main>
    </div>
  );
};

export default PosLayout;
